export interface QiyunYearMonth { years: number; months: number; }

export interface QiyunFallbackInput {
  qiYunInfo?: string;
  birthTime?: string;
  jiaoYunInfo?: string;
  lang?: string;
}

interface DateParts { y: number; m: number; d: number; h: number; min: number; }

interface QiyunYmdParts { years: number; months: number; days: number; }

function parseQiyunYmdParts(qiYunInfo: string): QiyunYmdParts | null {
  if (!qiYunInfo) return null;
  const y = /(\d+)年/.exec(qiYunInfo);
  const mo = /(\d+)个月/.exec(qiYunInfo);
  const d = /(\d+)天/.exec(qiYunInfo);
  if (!y && !mo && !d) return null;
  return {
    years: y ? Number(y[1]) : 0,
    months: mo ? Number(mo[1]) : 0,
    days: d ? Number(d[1]) : 0,
  };
}

export function parseQiyunYearMonth(qiYunInfo: string): QiyunYearMonth | null {
  const p = parseQiyunYmdParts(qiYunInfo);
  if (!p || (p.years === 0 && p.months === 0)) return null;
  return { years: p.years, months: p.months };
}

function formatYmd(years: number, months: number, days: number, lang: string): string | null {
  if (years === 0 && months === 0 && days === 0) return null;
  const zh: string[] = [];
  const en: string[] = [];
  const ko: string[] = [];
  const ja: string[] = [];
  if (years > 0) {
    zh.push(`${years}年`);
    en.push(`${years}y`);
    ko.push(`${years}년`);
    ja.push(`${years}年`);
  }
  if (months > 0) {
    zh.push(`${months}个月`);
    en.push(`${months}mo`);
    ko.push(`${months}개월`);
    ja.push(`${months}ヶ月`);
  }
  if (days > 0) {
    zh.push(`${days}天`);
    en.push(`${days}d`);
    ko.push(`${days}일`);
    ja.push(`${days}日`);
  }
  if (lang === 'en') return `${en.join(' ')} later`;
  if (lang === 'ko') return `${ko.join(' ')} 후`;
  if (lang === 'ja') return `${ja.join('')}後`;
  return `出生后${zh.join('')}起运`;
}

export function formatQiyunCollapsedLabel(qiYunInfo: string, lang: string): string | null {
  const p = parseQiyunYmdParts(qiYunInfo);
  if (!p) return null;
  return formatYmd(p.years, p.months, p.days, lang);
}

function toMs(p: DateParts): number {
  return Date.UTC(p.y, p.m - 1, p.d, p.h, p.min);
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function addMonthsClamped(birth: DateParts, months: number): DateParts {
  const total = birth.y * 12 + (birth.m - 1) + months;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return { y, m, d: Math.min(birth.d, daysInMonth(y, m)), h: birth.h, min: birth.min };
}

function diffToYmd(birth: DateParts, jiao: DateParts): QiyunYmdParts | null {
  let months = (jiao.y - birth.y) * 12 + (jiao.m - birth.m);
  if (months < 0) return null;
  let anchor = addMonthsClamped(birth, months);
  if (toMs(anchor) > toMs(jiao)) {
    months -= 1;
    if (months < 0) return null;
    anchor = addMonthsClamped(birth, months);
  }
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  const days = Math.max(0, Math.floor((toMs(jiao) - toMs(anchor)) / 86400000));
  if (years === 0 && remMonths === 0 && days === 0) return null;
  return { years, months: remMonths, days };
}

function parseBirthDate(birthTime?: string): DateParts | null {
  if (!birthTime) return null;
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/.exec(birthTime);
  if (!m) return null;
  return {
    y: Number(m[1]),
    m: Number(m[2]),
    d: Number(m[3]),
    h: m[4] ? Number(m[4]) : 0,
    min: m[5] ? Number(m[5]) : 0,
  };
}

function parseFirstJiaoDate(jiaoYunInfo?: string): DateParts | null {
  if (!jiaoYunInfo) return null;
  const m = /(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/.exec(jiaoYunInfo);
  if (!m) return null;
  return {
    y: Number(m[1]),
    m: Number(m[2]),
    d: Number(m[3]),
    h: m[4] ? Number(m[4]) : 0,
    min: m[5] ? Number(m[5]) : 0,
  };
}

export function resolveQiyunCollapsedLabel(input: QiyunFallbackInput): string | null {
  const lang = input.lang ?? 'zh-CN';
  if (input.qiYunInfo) {
    const viaQiYun = formatQiyunCollapsedLabel(input.qiYunInfo, lang);
    if (viaQiYun !== null) return viaQiYun;
  }
  const birth = parseBirthDate(input.birthTime);
  const jiao = parseFirstJiaoDate(input.jiaoYunInfo);
  if (!birth || !jiao) return null;
  const ymd = diffToYmd(birth, jiao);
  if (!ymd) return null;
  return formatYmd(ymd.years, ymd.months, ymd.days, lang);
}
